/**
 * /admin/leads — placeholder list. Step 8 replaces this with the
 * Kanban pipeline; for now it's a flat sortable list of every lead
 * with the basics so Step 2's intake screens have a sensible
 * landing page.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Upload, RefreshCw, Search, Loader2 } from "lucide-react";

import { listLeads } from "../../../api/leadEngine.api";
import { toArray } from "../../../utils/toArray";

const STATE_TONE = {
  fed:               "bg-slate-500/15 text-slate-300 border-slate-500/40",
  contacted:         "bg-blue-500/15 text-blue-300 border-blue-500/40",
  in_conversation:   "bg-amber-500/15 text-amber-300 border-amber-500/40",
  meeting_scheduled: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  trial:             "bg-purple-500/15 text-purple-300 border-purple-500/40",
  won:               "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  lost:              "bg-rose-500/15 text-rose-300 border-rose-500/40",
  parked:            "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
  not_now:           "bg-amber-500/10 text-amber-200 border-amber-500/30",
  hard_no:           "bg-rose-500/10 text-rose-200 border-rose-500/30",
  unsubscribed:      "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

export default function LeadsList() {
  // Search lives in the URL (?q=…) so navigating away + back, sharing
  // a link, or refreshing all preserve the filter.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";

  const [leads, setLeads] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  function setSearch(next) {
    const sp = new URLSearchParams(searchParams);
    if (next) sp.set("q", next);
    else sp.delete("q");
    setSearchParams(sp, { replace: true });
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await listLeads({ search: search || undefined });
      setLeads(toArray(data));
      setCount(data?.count || data?.results?.length || 0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Leads</h1>
          <p className="text-sm text-text-secondary mt-1">
            {count.toLocaleString("en-IN")} fed. AI outreach pipeline preview — full Kanban ships in a later step.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/leads/add"
            className="px-3 py-2 text-sm rounded-md bg-button-primary text-white flex items-center gap-1.5 hover:brightness-110"
          >
            <Plus className="w-4 h-4" /> Add lead
          </Link>
          <Link
            to="/admin/leads/import"
            className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5"
          >
            <Upload className="w-4 h-4" /> Import CSV
          </Link>
          <button
            onClick={load}
            className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / email / org…"
            className="pl-8 pr-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary w-72"
          />
        </div>
      </div>

      <div className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-table-header text-left text-[11px] uppercase tracking-widest text-text-secondary">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Org</th>
              <th className="px-3 py-2">Segment</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2 text-right">Added</th>
            </tr>
          </thead>
          <tbody>
            {loading && leads.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-secondary">
                <Loader2 className="w-4 h-4 inline-block mr-2 animate-spin" />
                Loading…
              </td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-secondary">
                No leads yet. <Link to="/admin/leads/add" className="text-button-primary hover:underline">Add the first one →</Link>
              </td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-border-default">
                <td className="px-3 py-2 font-medium">{l.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.email}</td>
                <td className="px-3 py-2 text-text-secondary">{l.org || "—"}</td>
                <td className="px-3 py-2 text-text-secondary text-xs">{l.segment}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${STATE_TONE[l.state] || "bg-bg-secondary text-text-secondary border-border-default"}`}>
                    {l.state}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-text-secondary text-xs">
                  {new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
